// Libs
import classNames from 'classnames/bind';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  timelineOppositeContentClasses,
  TimelineSeparator,
} from '@mui/lab';
import { Box, Button, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
// Others
import { IActivity } from '~/utils/interface/activity';
import { ActivityTypeEnum, DateFormatEnum } from '~/utils/enum/common';
import { formatDate, getFullName } from '~/utils/helper/common';
// Styles, images, icons
import styles from './BaseTimeLineItem.module.scss';

type Props = {
  activityData: IActivity[];
  date?: string;
  title?: string;
  desc?: string;
  onView?: (activity: IActivity) => void;
};

const cx = classNames.bind(styles);

const BaseTimeLineItem = (props: Props) => {
  //#region Destructuring Props
  const { activityData, onView } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/activity', 'common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const groupedActivities = useMemo(() => {
    const grouped: Record<string, typeof activityData> = {};

    activityData.forEach((activity) => {
      const activityDate = formatDate(activity.createdAt, DateFormatEnum.YYYY_MM_DD);
      if (!grouped[activityDate]) {
        grouped[activityDate] = [];
      }
      grouped[activityDate].push(activity);
    });

    return Object.entries(grouped).map(([date, activityList]) => ({
      date,
      activityList,
    }));
  }, [activityData]);

  const renderTitle = (actionType: string) => {
    switch (actionType) {
      case ActivityTypeEnum.CREATE_INVENTORY:
        return t('activity_title_create_inventory');

      case ActivityTypeEnum.UPDATE_INVENTORY:
        return t('activity_title_update_inventory');

      case ActivityTypeEnum.DELETE_INVENTORY:
        return t('activity_title_delete_inventory');

      default:
        return actionType;
    }
  };

  const handleViewDetail = (activity: IActivity) => {
    if (!activity) return;

    onView && onView(activity);
  };
  //#endregion Handle Function

  return (
    <Timeline
      sx={{
        [`& .${timelineOppositeContentClasses.root}`]: {
          flex: 0.1,
        },
      }}
      className={cx('timeline')}
    >
      {Array.isArray(groupedActivities) &&
        groupedActivities?.map(({ date, activityList }) => (
          <Box key={date}>
            <TimelineItem>
              <TimelineOppositeContent
                className={cx('timelineOPpositeContent')}
                align='right'
                variant='body2'
                color='text.secondary'
              >
                <Typography className={cx('timelineDate')}>{formatDate(date)}</Typography>
              </TimelineOppositeContent>

              <TimelineSeparator className={cx('timelineSeparator')}>
                <TimelineDot variant='filled' color='primary'></TimelineDot>

                <TimelineConnector className={cx('timelineConnector')} />
              </TimelineSeparator>

              <TimelineContent className={cx('timelineContent')} />
            </TimelineItem>

            {activityList?.map((activity, index) => (
              <TimelineItem key={activity?.id}>
                <TimelineOppositeContent
                  className={cx('timelineOppositeContentSecond')}
                  align='right'
                  variant='body2'
                  color='text.secondary'
                >
                  <Typography className={cx('timeLineTime')}>
                    {formatDate(activity?.createdAt, DateFormatEnum.HH_mm_A)}
                  </Typography>
                </TimelineOppositeContent>

                <TimelineSeparator className={cx('timelineSeparator')}>
                  <TimelineDot variant='filled' color='primary'></TimelineDot>

                  {index !== activityList.length - 1 && <TimelineConnector className={cx('timelineConnector')} />}
                </TimelineSeparator>

                <TimelineContent className={cx('timelineContent')}>
                  <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={6}>
                    <Stack gap={2}>
                      <Typography variant='h6' className={cx('timelineContentTitle')}>
                        {renderTitle(activity?.title)}
                      </Typography>

                      {activity?.createdBy && (
                        <Typography className={cx('timelineContentDesc')}>
                          {t('activity_created_by', {
                            ns: 'common/base',
                            name: getFullName({ ...activity?.createdBy }),
                          })}
                        </Typography>
                      )}
                    </Stack>

                    <Button
                      variant='outlined'
                      className={cx('buttonDetail')}
                      onClick={() => handleViewDetail(activity)}
                    >
                      {t('common_action_view_details', { ns: 'common/base' })}
                    </Button>
                  </Stack>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Box>
        ))}
    </Timeline>
  );
};

export default BaseTimeLineItem;
